import React, { useMemo, useRef } from "react"
import { Canvas, useFrame } from "@react-three/fiber"
import { Environment, useTexture } from "@react-three/drei"
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js"
import { Evaluator, Brush, SUBTRACTION } from "three-bvh-csg"
import * as THREE from "three"

function CubeMesh() {
  const meshRef = useRef()

  const geometry = useMemo(() => {
    const evaluator = new Evaluator()

    const cube = new Brush(new RoundedBoxGeometry(2, 2, 2, 4, 0.08))
    const sphere = new Brush(new THREE.SphereGeometry(1.2, 64, 64))
    cube.updateMatrixWorld()
    sphere.updateMatrixWorld()
    let result = evaluator.evaluate(cube, sphere, SUBTRACTION)

    // Bevel each hole opening with a torus subtraction
    const holeR = Math.sqrt(1.2 * 1.2 - 1.0 * 1.0) // ≈ 0.663
    const torusGeom = new THREE.TorusGeometry(holeR, 0.06, 16, 48)
    const faces = [
      { pos: [0, 0,  1], rot: [0, 0, 0] },
      { pos: [0, 0, -1], rot: [0, 0, 0] },
      { pos: [0,  1, 0], rot: [Math.PI / 2, 0, 0] },
      { pos: [0, -1, 0], rot: [Math.PI / 2, 0, 0] },
      { pos: [ 1, 0, 0], rot: [0, Math.PI / 2, 0] },
      { pos: [-1, 0, 0], rot: [0, Math.PI / 2, 0] },
    ]
    for (const { pos, rot } of faces) {
      const torus = new Brush(torusGeom)
      torus.position.set(pos[0], pos[1], pos[2])
      torus.rotation.set(rot[0], rot[1], rot[2])
      torus.updateMatrixWorld()
      result = evaluator.evaluate(result, torus, SUBTRACTION)
    }

    cube.geometry.dispose()
    sphere.geometry.dispose()
    torusGeom.dispose()
    return result.geometry
  }, [])

  const envTexture = useTexture("/texture.jpeg")
  envTexture.mapping = THREE.EquirectangularReflectionMapping

  useFrame((_, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += delta * 0.15
      meshRef.current.rotation.y += delta * 0.22
    }
  })

  return (
    <mesh ref={meshRef} geometry={geometry} scale={0.6} position={[2, 0, 0]}>
      <meshStandardMaterial
        metalness={1}
        roughness={0.08}
        envMap={envTexture}
        envMapIntensity={2.5}
        color="#e0e0e0"
        side={THREE.DoubleSide}
      />
    </mesh>
  )
}

function Lights() {
  return (
    <>
      {/* Key: warm, bright, upper-left front */}
      <pointLight position={[-4, 5, 5]} intensity={60} color="#fff5e0" />
      {/* Fill: cool, dimmer, right */}
      <pointLight position={[5, 1, 3]} intensity={15} color="#b8ccff" />
      {/* Rim: back-bottom for edge catch */}
      <pointLight position={[0, -4, -5]} intensity={40} color="#ffffff" />
    </>
  )
}

export function CubeScene() {
  return (
    <Canvas
      gl={{ alpha: true, antialias: true }}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background: "transparent",
        zIndex: 1,
      }}
      camera={{ position: [0, 0, 6], fov: 35 }}
    >
      <Lights />
      <React.Suspense fallback={null}>
        <CubeMesh />
      </React.Suspense>
    </Canvas>
  )
}
